/**
 * Joystick UI Manager
 * **INTERNAL CLASS - DO NOT USE DIRECTLY**
 * 
 * Handles joystick instance creation and management
 * Use UIManager instead for all UI operations
 */

import { JoystickInstance } from '../components/JoystickInstance';
import type { JoystickUIConfig, IJoystick } from '../types';
import type { IUIManager } from './IUIManager';

export class JoystickUIManager implements IUIManager<IJoystick, JoystickUIConfig> {
    private joysticks: Map<string, IJoystick> = new Map();

    /**
     * Create a joystick instance
     */
    create(config: JoystickUIConfig): IJoystick {
        // Remove existing joystick with same ID
        if (this.joysticks.has(config.id)) {
            this.joysticks.get(config.id)!.remove();
        }

        const joystick = new JoystickInstance(config);
        this.joysticks.set(config.id, joystick);
        return joystick;
    }

    /**
     * Get a joystick instance by ID
     */
    get(id: string): IJoystick | null {
        return this.joysticks.get(id) ?? null;
    }

    /**
     * Check if a joystick exists
     */
    has(id: string): boolean {
        return this.joysticks.has(id);
    }

    /**
     * Remove a specific joystick by ID
     */
    remove(id: string): boolean {
        const joystick = this.joysticks.get(id);
        if (joystick) {
            joystick.remove();
            this.joysticks.delete(id);
            return true;
        }
        return false;
    }

    /**
     * Dispose all joysticks
     */
    dispose(): void {
        this.joysticks.forEach(joystick => joystick.remove());
        this.joysticks.clear();
    }
}
